import { useState, useEffect } from 'react'
import type { Order } from '../types/Order'
import type { InspectionRecord } from '../types/InspectionRecord'
import { getNextInspectionTime } from '../types/Order'

// Mock inspection data generator
function generateMockInspections(
  orderId: string,
  count: number,
  orderData: Partial<Order>,
): InspectionRecord[] {
  const inspections: InspectionRecord[] = []
  const now = new Date()

  for (let i = 0; i < count; i++) {
    const minutesAgo = (count - i) * 30 // 30 min intervals
    const datum = new Date(now.getTime() - minutesAgo * 60000)

    // Simulate some temperature warnings
    const hasWarning = i === 2 || i === 5
    const temperatur = hasWarning ? 4.2 : 5.5 + Math.random() * 1.5

    inspections.push({
      id: `${orderId}-${i}`,
      personalNummer: hasWarning ? '999999' : '120938',
      personalName: hasWarning ? 'Supervisor Schmidt' : 'Max Mustermann',
      chargennummer: orderData.chargenNummer || '',
      produktNr: orderData.produktNr || '',
      linie: orderData.linie || '',
      sollgewicht: orderData.sollgewicht || '',
      referenztemperatur: '5-7°C',
      intervallzeit: 30,
      gewicht: 2.45 + Math.random() * 0.1,
      temperatur: temperatur,
      datum: datum,
      chipNr: hasWarning ? 'CHIP-SUP-001' : undefined,
      fehlerMeldung: hasWarning
        ? 'Referenztemperatur unterschritten - Quittiert durch Supervisor'
        : undefined,
      isQuittiert: hasWarning,
    })
  }

  return inspections.reverse() // Most recent first
}

// Mock data generator
function generateMockOrders(): Order[] {
  const now = new Date()

  return [
    {
      id: '1',
      chargenNummer: 'CH-2024-001',
      produktName: 'P-123123',
      produktNr: '123123',
      status: 'active',
      linie: 'Linie 1',
      sollgewicht: '2.5 kg',
      prueflingeGeprueft: 12,
      avgTemperature: 6.2,
      avgWeight: 2.48,
      lastInspection: new Date(now.getTime() - 15 * 60000),
      nextInspection: new Date(now.getTime() + 15 * 60000),
      startedAt: new Date(now.getTime() - 45 * 60000),
    },
    {
      id: '2',
      chargenNummer: 'CH-2024-002',
      produktName: 'P-456789',
      produktNr: '456789',
      status: 'active',
      linie: 'Linie 3',
      sollgewicht: '3.2 kg',
      prueflingeGeprueft: 8,
      avgTemperature: 5.8,
      avgWeight: 3.15,
      lastInspection: new Date(now.getTime() - 5 * 60000),
      nextInspection: new Date(now.getTime() + 25 * 60000),
      startedAt: new Date(now.getTime() - 35 * 60000),
    },
    {
      id: '3',
      chargenNummer: 'CH-2024-003',
      produktName: 'P-789012',
      produktNr: '789012',
      status: 'paused',
      linie: 'Linie 2',
      sollgewicht: '1.8 kg',
      prueflingeGeprueft: 24,
      avgTemperature: 6.5,
      avgWeight: 1.82,
      lastInspection: new Date(now.getTime() - 120 * 60000),
      nextInspection: new Date(now.getTime() + 30 * 60000),
      startedAt: new Date(now.getTime() - 180 * 60000),
    },
    {
      id: '4',
      chargenNummer: 'CH-2024-004',
      produktName: 'P-345678',
      produktNr: '345678',
      status: 'completed',
      linie: 'Linie 4',
      sollgewicht: '2.0 kg',
      prueflingeGeprueft: 45,
      avgTemperature: 6.1,
      avgWeight: 2.02,
      lastInspection: new Date(now.getTime() - 60 * 60000),
      nextInspection: new Date(now.getTime()),
      startedAt: new Date(now.getTime() - 180 * 60000),
      completedAt: new Date(now.getTime() - 30 * 60000),
    },
    {
      id: '5',
      chargenNummer: 'CH-2024-005',
      produktName: 'P-901234',
      produktNr: '901234',
      status: 'completed',
      linie: 'Linie 5',
      sollgewicht: '4.5 kg',
      prueflingeGeprueft: 67,
      avgTemperature: 5.9,
      avgWeight: 4.52,
      lastInspection: new Date(now.getTime() - 90 * 60000),
      nextInspection: new Date(now.getTime()),
      startedAt: new Date(now.getTime() - 240 * 60000),
      completedAt: new Date(now.getTime() - 60 * 60000),
    },
  ]
}

export function useOrders() {
  const [orders, setOrders] = useState<Order[]>([])
  const [showCompleted, setShowCompleted] = useState(false)
  const [inspectionsCache, setInspectionsCache] = useState<
    Record<string, InspectionRecord[]>
  >({})

  useEffect(() => {
    const mockOrders = generateMockOrders()
    setOrders(mockOrders)

    // Generate mock inspections for each order
    const cache: Record<string, InspectionRecord[]> = {}
    mockOrders.forEach((order) => {
      cache[order.id] = generateMockInspections(
        order.id,
        order.prueflingeGeprueft,
        order,
      )
    })
    setInspectionsCache(cache)
  }, [])

  const activeOrders = orders.filter((o) => o.status === 'active')
  const pausedOrders = orders.filter((o) => o.status === 'paused')
  const completedOrders = orders.filter((o) => o.status === 'completed')

  const filteredOrders = showCompleted
    ? orders
    : orders.filter((o) => o.status !== 'completed')

  const getInspections = (orderId: string): InspectionRecord[] => {
    return inspectionsCache[orderId] || []
  }

  return {
    orders: filteredOrders,
    activeOrders: activeOrders.length > 0 ? activeOrders : [],
    pausedOrders: pausedOrders.length > 0 ? pausedOrders : [],
    completedOrders:
      showCompleted && completedOrders.length > 0 ? completedOrders : [],
    showCompleted,
    toggleCompleted: () => setShowCompleted(!showCompleted),
    counts: {
      active: activeOrders.length,
      paused: pausedOrders.length,
      completed: completedOrders.length,
    },
    getInspections,
  }
}
